// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Link, useNavigate } from "@tanstack/react-router";
import type { ColumnDef, PaginationState, SortingState } from "@tanstack/react-table";
import { Building2, Store, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/api/client";
import { type SettingEntry, useSettingsQuery } from "@/api/hooks/use-settings";
import { DataTable } from "@/components/data-table";
import { PageHeader } from "@/components/layout/page-header";
import { CopyableKey } from "@/components/shared/copyable-key";
import { FilterSelect } from "@/components/shared/filter-select";
import { FilterToggle } from "@/components/shared/filter-toggle";
import { GroupBadge } from "@/components/shared/group-badge";
import { KeyChip } from "@/components/shared/key-chip";
import { RowId } from "@/components/shared/row-id";
import { Button } from "@/components/ui/button";
import { FieldTypeBadge } from "@/features/settings/field-type-badge";
import { SettingValueCell } from "@/features/settings/setting-value-cell";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";
import { useEscapeToClearFilters } from "@/hooks/use-escape-to-clear-filters";
import { downloadCsv } from "@/lib/csv";
import { decodeFilters, encodeFilters } from "@/lib/filter-params";
import { getDefaultPageSize } from "@/lib/preferences";
import { Route } from "@/routes/_authenticated/settings/index";

const FILTER_DEFAULTS = {
  search: "",
  group: "",
  onlyChainOverrides: false,
  onlyStoreOverrides: false,
  page: 0,
  get pageSize() {
    return getDefaultPageSize();
  },
  sortId: "settingId",
  sortDesc: false,
};

export function SettingsList() {
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const { f } = Route.useSearch();
  const initial = useMemo(() => decodeFilters(f, FILTER_DEFAULTS), [f]);
  const { data: context, isLoading: contextLoading } = useBackofficeContext();

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initial.page,
    pageSize: initial.pageSize,
  });
  const [sorting, setSorting] = useState<SortingState>(
    initial.sortId !== "settingId" || initial.sortDesc ? [{ id: initial.sortId, desc: initial.sortDesc }] : [],
  );
  const [search, setSearch] = useState(initial.search);
  const [group, setGroup] = useState<string>(initial.group);
  const [onlyChainOverrides, setOnlyChainOverrides] = useState(initial.onlyChainOverrides);
  const [onlyStoreOverrides, setOnlyStoreOverrides] = useState(initial.onlyStoreOverrides);

  const isFiltered = !!(group || search || onlyChainOverrides || onlyStoreOverrides);

  const clearFilters = useCallback(function clearFilters() {
    setGroup("");
    setSearch("");
    setOnlyChainOverrides(false);
    setOnlyStoreOverrides(false);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  useEscapeToClearFilters({ isFiltered, clearFilters });

  const syncUrl = useCallback(
    (overrides: Partial<typeof FILTER_DEFAULTS> = {}) => {
      const current = {
        search,
        group,
        onlyChainOverrides,
        onlyStoreOverrides,
        page: pagination.pageIndex,
        pageSize: pagination.pageSize,
        sortId: sorting[0]?.id ?? "settingId",
        sortDesc: sorting[0]?.desc ?? false,
        ...overrides,
      };
      const encoded = encodeFilters(current, FILTER_DEFAULTS);
      navigate({
        to: "/settings",
        search: encoded ? { f: encoded } : {},
        replace: true,
      });
    },
    [
      search,
      group,
      onlyChainOverrides,
      onlyStoreOverrides,
      pagination.pageIndex,
      pagination.pageSize,
      sorting,
      navigate,
    ],
  );

  useEffect(
    function synchronizeFiltersToUrl() {
      syncUrl();
    },
    [syncUrl],
  );

  const sortBy = (sorting[0]?.id ?? "settingId") as
    | "settingId"
    | "name"
    | "key"
    | "description"
    | "groupName"
    | "defaultValue"
    | "chainOverridesCount"
    | "storeOverridesCount";
  const orderBy = sorting[0]?.desc ? ("desc" as const) : ("asc" as const);

  const { data, isLoading, refetch, isFetching } = useSettingsQuery({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: search || undefined,
    sortBy,
    orderBy,
    group: group || undefined,
    onlyChainOverrides: onlyChainOverrides || undefined,
    onlyStoreOverrides: onlyStoreOverrides || undefined,
  });

  const handleExport = useCallback(async () => {
    const res = await api.GET("/v1/settings", {
      params: {
        query: {
          page: 1,
          limit: -1,
          search: search || undefined,
          sortBy,
          orderBy,
          "filter[group]": group || undefined,
          "filter[onlyChainOverrides]": onlyChainOverrides || undefined,
          "filter[onlyStoreOverrides]": onlyStoreOverrides || undefined,
        },
      },
    });
    const items = (res.data as unknown as { settings: SettingEntry[] }).settings;
    downloadCsv(
      "settings.csv",
      ["ID", "Key", "Name", "Group", "Type", "Default Value", "Chain Overrides", "Store Overrides"],
      items.map((s) => [
        String(s.settingId),
        s.key,
        s.name,
        s.groupName,
        s.fieldType,
        s.defaultValue ?? "",
        String(s.chainOverridesCount),
        String(s.storeOverridesCount),
      ]),
    );
  }, [search, sortBy, orderBy, group, onlyChainOverrides, onlyStoreOverrides]);

  const groups = useMemo(() => {
    return [...(context?.storeSettingsGroups ?? [])].sort();
  }, [context]);

  const columns = useMemo<ColumnDef<SettingEntry, unknown>[]>(
    () => [
      {
        accessorKey: "settingId",
        header: t("settings.list.columns.id"),
        size: 80,
        enableHiding: false,
        meta: { columnLabel: t("settings.list.columns.id"), className: "pl-4", forceWidth: true, sticky: "left" },
        cell: ({ row }) => <RowId id={row.original.settingId} />,
      },
      {
        accessorKey: "name",
        header: t("settings.list.columns.name"),
        meta: { columnLabel: t("settings.list.columns.name") },
        cell: ({ row, table }) => {
          // Surface the key as a sub-line when its dedicated column is hidden from the view.
          const keyColumnVisible = table.getColumn("key")?.getIsVisible() ?? false;
          return (
            <div className="flex flex-col">
              <Link
                to="/settings/$settingName"
                params={{ settingName: row.original.key }}
                className="font-medium text-primary hover:underline"
              >
                {row.original.name}
              </Link>
              {!keyColumnVisible && <CopyableKey value={row.original.key} />}
            </div>
          );
        },
      },
      {
        accessorKey: "key",
        header: t("settings.list.columns.key"),
        enableSorting: true,
        meta: { columnLabel: t("settings.list.columns.key"), defaultHidden: true },
        cell: ({ row }) => <KeyChip label={row.original.key} />,
      },
      {
        accessorKey: "groupName",
        header: t("settings.list.columns.groupName"),
        meta: { columnLabel: t("settings.list.columns.groupName") },
        cell: ({ row }) => <GroupBadge name={row.original.groupName} />,
      },
      {
        accessorKey: "fieldType",
        header: t("settings.list.columns.fieldType"),
        size: 90,
        enableSorting: false,
        meta: { columnLabel: t("settings.list.columns.fieldType"), forceWidth: true },
        cell: ({ row }) => <FieldTypeBadge type={row.original.fieldType} />,
      },
      {
        accessorKey: "defaultValue",
        header: t("settings.list.columns.defaultValue"),
        meta: { columnLabel: t("settings.list.columns.defaultValue") },
        cell: ({ row }) => (
          <SettingValueCell
            value={row.original.defaultValue}
            fieldType={row.original.fieldType}
            name={row.original.name}
          />
        ),
      },
      {
        accessorKey: "chainOverridesCount",
        header: t("settings.list.columns.chainOverrides"),
        size: 130,
        meta: { columnLabel: t("settings.list.columns.chainOverrides") },
        cell: ({ row }) =>
          row.original.chainOverridesCount > 0 ? (
            <Link
              to="/settings/$settingName"
              params={{ settingName: row.original.key }}
              search={{ t: "chains" }}
              className="text-primary hover:underline"
            >
              {row.original.chainOverridesCount}
            </Link>
          ) : (
            <span className="text-muted-foreground">0</span>
          ),
      },
      {
        accessorKey: "storeOverridesCount",
        header: t("settings.list.columns.storeOverrides"),
        size: 130,
        meta: { columnLabel: t("settings.list.columns.storeOverrides") },
        cell: ({ row }) =>
          row.original.storeOverridesCount > 0 ? (
            <Link
              to="/settings/$settingName"
              params={{ settingName: row.original.key }}
              search={{ t: "stores" }}
              className="text-primary hover:underline"
            >
              {row.original.storeOverridesCount}
            </Link>
          ) : (
            <span className="text-muted-foreground">0</span>
          ),
      },
    ],
    [t],
  );

  return (
    <div>
      <PageHeader title={t("settings.title")} />
      <div className="p-6">
        <DataTable
          storageKey="settings-list"
          columns={columns}
          data={data?.settings ?? []}
          totalCount={data?.count ?? 0}
          isLoading={isLoading}
          pagination={pagination}
          onPaginationChange={setPagination}
          sorting={sorting}
          onSortingChange={setSorting}
          search={search}
          onSearchChange={setSearch}
          searchDebounceMs={1000}
          onRefresh={refetch}
          isRefreshing={isFetching}
          onExport={handleExport}
          searchPlaceholder={t("settings.list.searchPlaceholder")}
          filters={
            <>
              <FilterSelect
                label={t("settings.list.filter.group")}
                value={group}
                onChange={(v) => {
                  setGroup(v);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                options={groups.map((g) => ({ label: g, value: g }))}
                searchPlaceholder={t("settings.list.filter.searchGroups")}
                emptyText={t("settings.list.filter.noGroups")}
                loading={contextLoading}
              />
              <FilterToggle
                active={onlyChainOverrides}
                onChange={() => {
                  setOnlyChainOverrides((v) => !v);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                icon={Building2}
                label={t("settings.list.filter.chainOverrides")}
              />
              <FilterToggle
                active={onlyStoreOverrides}
                onChange={() => {
                  setOnlyStoreOverrides((v) => !v);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                icon={Store}
                label={t("settings.list.filter.storeOverrides")}
              />
              {isFiltered && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-muted-foreground hover:text-foreground"
                  onClick={clearFilters}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </>
          }
        />
      </div>
    </div>
  );
}
